/**
 * Custom hook for fetching and caching map layers
 * Provides debouncing, caching, and progressive loading
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../apiClient';

// In-memory cache for layer data
const layerCache = new Map();

/**
 * Hook for fetching layers with caching and optimization
 * @param {string} layerName - Name of the layer (e.g., 'surfaces')
 * @param {object} options - Fetch options
 * @returns {object} - { data, loading, error, refetch }
 */
export function useLayerData(layerName, options = {}) {
  const {
    enabled = true,
    centerLat = null,
    centerLon = null,
    radiusKm = 100,
    zoom = 18,
    forAnalysis = false,
    debounceMs = 500
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(null);
  
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastFetchParamsRef = useRef(null);

  /**
   * Generate cache key from fetch parameters
   */
  const getCacheKey = useCallback((params) => {
    return `${layerName}:${params.centerLat}:${params.centerLon}:${params.radiusKm}:${params.zoom}:${params.forAnalysis}`;
  }, [layerName]);

  /**
   * Fetch layer data from API
   */
  const fetchLayer = useCallback(async (params, signal) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.centerLat !== null && params.centerLon !== null) {
        queryParams.append('centerLat', params.centerLat);
        queryParams.append('centerLon', params.centerLon);
        queryParams.append('radiusKm', params.radiusKm);
      }
      queryParams.append('zoom', params.zoom);
      if (params.forAnalysis) {
        queryParams.append('forAnalysis', 'true');
      }

      const cacheKey = getCacheKey(params);
      
      // Check cache first
      const cached = layerCache.get(cacheKey);
      if (cached && cached.version === version) {
        return cached.data;
      }

      const url = `/${layerName}/ALL?${queryParams.toString()}`;
      
      // Add ETag header if we have cached data
      const headers = {};
      if (cached && cached.etag) {
        headers['If-None-Match'] = cached.etag;
      }

      const response = await apiFetch(url, { 
        signal,
        headers 
      });

      // Handle 304 Not Modified
      if (response.status === 304) {
        return cached.data;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch ${layerName}: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Get ETag and version from response
      const etag = response.headers.get('ETag');
      const responseVersion = response.headers.get('X-Layer-Version') || responseData.version;

      // Update cache
      layerCache.set(cacheKey, {
        data: responseData,
        etag,
        version: responseVersion,
        timestamp: Date.now()
      });

      return responseData;
    } catch (err) {
      if (err.name === 'AbortError') {
        throw err;
      }
      console.error(`Error fetching ${layerName}:`, err);
      throw err;
    }
  }, [layerName, version, getCacheKey]);

  /**
   * Trigger data fetch with debouncing
   */
  const refetch = useCallback(() => {
    if (!enabled) {
      return;
    }

    const params = {
      centerLat,
      centerLon,
      radiusKm,
      zoom,
      forAnalysis
    };

    // Check if parameters changed
    const paramsStr = JSON.stringify(params);
    if (lastFetchParamsRef.current === paramsStr && data !== null) {
      return;
    }
    lastFetchParamsRef.current = paramsStr;

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Abort existing fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setLoading(true);
    setError(null);

    // Debounce the fetch
    debounceTimerRef.current = setTimeout(async () => {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const result = await fetchLayer(params, abortController.signal);
        setData(result);
        setVersion(result.version);
        setError(null);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }, debounceMs);
  }, [enabled, centerLat, centerLon, radiusKm, zoom, forAnalysis, debounceMs, fetchLayer, data]);

  /**
   * Effect to trigger fetch when parameters change
   */
  useEffect(() => {
    refetch();
    
    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [refetch]);

  /**
   * Manual invalidate cache and refetch
   */
  const invalidateAndRefetch = useCallback(async () => {
    // Clear cache for this layer
    for (const key of layerCache.keys()) {
      if (key.startsWith(`${layerName}:`)) {
        layerCache.delete(key);
      }
    }
    
    // Invalidate on server
    try {
      await apiFetch('/layers/invalidate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ layerName })
      });
    } catch (err) {
      console.warn('Failed to invalidate server cache:', err);
    }
    
    // Refetch
    lastFetchParamsRef.current = null;
    refetch();
  }, [layerName, refetch]);

  return {
    data,
    loading,
    error,
    version,
    refetch,
    invalidateAndRefetch
  };
}

/**
 * Clear all cached layer data
 */
export function clearLayerCache() {
  layerCache.clear();
}
